/**
 * Apparence des sous-parties du bloc « Filtres catalogue ».
 * KEEP IN SYNC avec inventozen-frontpublic/src/lib/cms-puck/blocks/catalogue-filters.ts
 */

import type { CSSProperties } from "react";
import {
  normalizeColorRef,
  resolveColorRef,
  type ColorRef,
} from "@/lib/cms-puck/block-style";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";

export type FilterPartColors = {
  background: ColorRef;
  text: ColorRef;
  border: ColorRef;
};

/** Couleurs éditables des 3 zones UI de la barre de filtres. */
export type CatalogueFiltersParts = {
  /** Groupe des selects (date, type, thèmes, mots-clés). */
  selects: FilterPartColors;
  /** Champ de recherche libre. */
  search: FilterPartColors;
  /** Bouton « Plus de filtres ». */
  moreButton: FilterPartColors;
};

/** Hex résolus pour le rendu (toolbar). */
export type FilterToolbarPartStyle = {
  backgroundColor?: string;
  color?: string;
  borderColor?: string;
};

export type FilterToolbarPartStyles = {
  selects?: FilterToolbarPartStyle;
  search?: FilterToolbarPartStyle;
  moreButton?: FilterToolbarPartStyle;
};

const EMPTY_PART: FilterPartColors = {
  background: null,
  text: null,
  border: null,
};

export const DEFAULT_CATALOGUE_FILTERS_PARTS: CatalogueFiltersParts = {
  selects: { ...EMPTY_PART },
  search: { ...EMPTY_PART },
  moreButton: { ...EMPTY_PART },
};

function normalizePart(value: unknown): FilterPartColors {
  const p = (value ?? {}) as Partial<FilterPartColors>;
  return {
    background: normalizeColorRef(p.background),
    text: normalizeColorRef(p.text),
    border: normalizeColorRef(p.border),
  };
}

export function normalizeCatalogueFiltersParts(
  value: unknown,
): CatalogueFiltersParts {
  const v = (value ?? {}) as Partial<CatalogueFiltersParts>;
  return {
    selects: normalizePart(v.selects),
    search: normalizePart(v.search),
    moreButton: normalizePart(v.moreButton),
  };
}

function resolvePart(
  palette: CmsColor[],
  part: FilterPartColors,
): FilterToolbarPartStyle | undefined {
  const backgroundColor = resolveColorRef(palette, part.background) ?? undefined;
  const color = resolveColorRef(palette, part.text) ?? undefined;
  const borderColor = resolveColorRef(palette, part.border) ?? undefined;
  if (!backgroundColor && !color && !borderColor) return undefined;
  return { backgroundColor, color, borderColor };
}

/** Résout les ColorRef → hex pour FilterToolbar / aperçu. */
export function resolveCatalogueFiltersPartStyles(
  palette: CmsColor[],
  parts: CatalogueFiltersParts | unknown,
): FilterToolbarPartStyles {
  const normalized = normalizeCatalogueFiltersParts(parts);
  return {
    selects: resolvePart(palette, normalized.selects),
    search: resolvePart(palette, normalized.search),
    moreButton: resolvePart(palette, normalized.moreButton),
  };
}

/** Style inline pour une zone (fond / texte / bordure). */
export function filterPartInlineStyle(
  part?: FilterToolbarPartStyle,
): CSSProperties | undefined {
  if (!part) return undefined;
  const style: CSSProperties = {};
  if (part.backgroundColor) style.backgroundColor = part.backgroundColor;
  if (part.color) style.color = part.color;
  if (part.borderColor) {
    style.borderColor = part.borderColor;
    style.borderWidth = 1;
    style.borderStyle = "solid";
  }
  return Object.keys(style).length ? style : undefined;
}
